// ============================================================
// VENDORED — do not edit here.
//
// Exact copy of public/admin/lib/package.js from the mesarobles repo at commit
// f4b8562 ("Merge PR #1: the Mesa Robles side of Share to Mesa Robles"),
// verified byte-identical to the copy shipped in Share-to-Mesa-Robles-v1.zip.
//
// This is the shared contract between Mesa Robles, LinksLab and PastramiLab:
// it decides what a valid recipe package is. Fixes belong upstream in the
// mesarobles repo and are then re-vendored here, header and all.
// ============================================================
// Mesa Robles Recipe Package — format v1.
//
// The contract between LinksLab, PastramiLab and Mesa Robles.
//   * Canonical copy: mesarobles repo, public/admin/lib/package.js (+ package.d.ts).
//   * LinksLab and PastramiLab VENDOR an exact copy (tests only; exporters build
//     the object themselves and must pass validatePackage()).
//
// Rules for this file: plain ES module, no dependencies, no window/document at
// module top level (Next imports it at build time in Node), explicit exports.
//
// Design decisions (Oct 2026):
//   * `display` is everything Mesa Robles shows. It is written in Mesa Robles'
//     own terms, so the site never runs calculator math.
//   * `calculator` is the app's own data, opaque to Mesa Robles, kept verbatim
//     so "Download to calculator" is lossless.
//   * Nitrite NEVER blocks an import. The calculator is the safety check; the
//     validator only raises warnings, and the page shows what the file says.

export const FORMAT = 'mesarobles-recipe-package';
export const PACKAGE_VERSION = 1;
export const APPS = /** @type {const} */ (['LinksLab', 'PastramiLab']);
export const APP_TOOL = { LinksLab: 'linkslab', PastramiLab: 'pastramilab' };
export const MAX_BYTES = 1_000_000;

/** Must stay identical to src/lib/sections.ts (dept + canonical slug). A test asserts it. */
export const SECTIONS = [
  { dept: 'table', slug: 'quick-good', name: 'Quick & Good' },
  { dept: 'table', slug: 'celebrations', name: 'Celebrations' },
  { dept: 'table', slug: 'snacks-salsas', name: 'Snacks & Salsas' },
  { dept: 'table', slug: 'sweet-sticky', name: 'Sweet & Sticky' },
  { dept: 'smoke', slug: 'bbq-grilling', name: 'BBQ & Grilling' },
  { dept: 'smoke', slug: 'curing-sausage', name: 'Curing & Sausage' },
  { dept: 'smoke', slug: 'technique-guides', name: 'Technique Guides' },
  { dept: 'cellar', slug: 'beer-recipes', name: 'Beer Recipes' },
  { dept: 'cellar', slug: 'brewing-knowledge', name: 'Brewing Knowledge' },
  { dept: 'cellar', slug: 'other-fermentations', name: 'Other Fermentations' },
  { dept: 'craft', slug: 'linkslab', name: 'LinksLab' },
  { dept: 'craft', slug: 'pastramilab', name: 'PastramiLab' },
  { dept: 'cure', slug: 'spice-blends-rubs', name: 'Spice Blends & Rubs' },
  { dept: 'cure', slug: 'sauces-marinades', name: 'Sauces & Marinades' },
  { dept: 'notes', slug: 'kitchen-foundations', name: 'Kitchen Foundations' },
  { dept: 'notes', slug: 'life-wisdom', name: 'Life Wisdom' },
];
/** The calculators' own landing pages, not places to import recipes into. */
export const NOT_IMPORT_TARGETS = ['linkslab', 'pastramilab'];

export const MARGIN_NOTE_TYPES = ['family_story', 'cooking_wisdom', 'life_wisdom', 'make_it_your_own'];
export const SOURCE_TYPES = ['family_original', 'brewpub_inspired', 'internet_professional', 'fusion_adaptation'];
export const VISIBILITY = ['public', 'unlisted'];
export const TIMING_KEYS = ['serves', 'hands_on_time', 'total_time', 'salt_time', 'keeps', 'heat_level'];
export const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// ── small helpers ──────────────────────────────────────────────────────────

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isStr = (v) => typeof v === 'string';
const isNonEmpty = (v) => isStr(v) && v.trim().length > 0;
const isStrOrNull = (v) => v === null || v === undefined || isStr(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const isNumOrNull = (v) => v === null || v === undefined || isNum(v);

/** "Pastrami Vietcoa — Dino / plate rib (bone-in)" → "pastrami-vietcoa-dino-plate-rib-bone-in" */
export function slugify(text) {
  return String(text ?? '')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '');
}

export function packageFilename(slugOrTitle) {
  const s = SLUG_RE.test(String(slugOrTitle)) ? slugOrTitle : slugify(slugOrTitle);
  return `${s || 'recipe'}.mesarobles.json`;
}

export function isValidPlacement(department, section) {
  return SECTIONS.some((s) => s.dept === department && s.slug === section);
}

// ── validation ─────────────────────────────────────────────────────────────

/**
 * @param {unknown} input  a parsed object, or the raw file text
 * @returns {{ok: true, pkg: object, warnings: string[]} | {ok: false, errors: string[], warnings: string[]}}
 */
export function validatePackage(input) {
  const errors = [];
  const warnings = [];
  let pkg = input;

  if (isStr(input)) {
    if (input.length > MAX_BYTES) errors.push(`File is ${input.length} bytes; the limit is ${MAX_BYTES}.`);
    try { pkg = JSON.parse(input); } catch (e) {
      return { ok: false, errors: [`Not valid JSON: ${e.message}`], warnings };
    }
  }
  if (!isObj(pkg)) return { ok: false, errors: ['The file is not a recipe package (expected a JSON object).'], warnings };

  // envelope
  if (pkg.format !== FORMAT) {
    if (pkg.app === 'pastramilab' && pkg.data) errors.push('This is a PastramiLab backup, not a recipe package. Use "Share to Mesa Robles" in PastramiLab.');
    else if (pkg.format === 'linkslab-backup') errors.push('This is a LinksLab backup, not a recipe package. Use "Share to Mesa Robles" in LinksLab.');
    else errors.push(`format must be "${FORMAT}".`);
    return { ok: false, errors, warnings };
  }
  if (!Number.isInteger(pkg.version) || pkg.version < 1) errors.push('version must be a positive integer.');
  else if (pkg.version > PACKAGE_VERSION) errors.push(`This file is format version ${pkg.version}; this reader understands up to ${PACKAGE_VERSION}. Update Mesa Robles first.`);
  if (pkg.kind !== 'recipe') errors.push('kind must be "recipe".');
  if (!isNonEmpty(pkg.exportedAt) || Number.isNaN(Date.parse(pkg.exportedAt))) errors.push('exportedAt must be an ISO date.');

  const src = pkg.source;
  if (!isObj(src)) errors.push('source is missing.');
  else {
    if (!APPS.includes(src.app)) errors.push(`source.app must be one of ${APPS.join(', ')}.`);
    if (!isNonEmpty(src.appUrl)) errors.push('source.appUrl is missing.');
    if (!isStrOrNull(src.build)) errors.push('source.build must be a string or null.');
  }

  const calc = pkg.calculator;
  if (!isObj(calc)) errors.push('calculator block is missing.');
  else {
    if (isObj(src) && calc.app !== src.app) errors.push('calculator.app must match source.app.');
    if (!Number.isInteger(calc.formatVersion) || calc.formatVersion < 1) errors.push('calculator.formatVersion must be a positive integer.');
    if (!isObj(calc.data)) errors.push('calculator.data must be an object.');
  }

  validateDisplay(pkg.display, errors, warnings);

  return errors.length ? { ok: false, errors, warnings } : { ok: true, pkg, warnings };
}

function validateDisplay(d, errors, warnings) {
  if (!isObj(d)) { errors.push('display block is missing.'); return; }
  const at = (p) => `display.${p}`;

  if (!isNonEmpty(d.title)) errors.push(`${at('title')} is required.`);
  else if (d.title.length > 140) warnings.push('The title is long; consider shortening it for the page heading.');
  for (const k of ['subtitle', 'summary', 'allergens', 'rescaleNote']) {
    if (!isStrOrNull(d[k])) errors.push(`${at(k)} must be text or null.`);
  }
  if (!isNumOrNull(d.elevationFt)) errors.push(`${at('elevationFt')} must be a number or null.`);

  if (d.suggested !== undefined && d.suggested !== null) {
    if (!isObj(d.suggested)) errors.push(`${at('suggested')} must be an object.`);
    else {
      const { department, section, slug } = d.suggested;
      if ((department || section) && !isValidPlacement(department, section)) {
        warnings.push(`Suggested placement "${department} / ${section}" is not a valid pair; choose one.`);
      }
      if (slug && !SLUG_RE.test(slug)) warnings.push(`Suggested slug "${slug}" is not URL-safe; it will be regenerated.`);
    }
  }

  if (d.timing !== undefined && d.timing !== null) {
    if (!isObj(d.timing)) errors.push(`${at('timing')} must be an object.`);
    else for (const [k, v] of Object.entries(d.timing)) {
      if (!TIMING_KEYS.includes(k)) warnings.push(`${at('timing')}.${k} is not a Mesa Robles timing field and will be dropped.`);
      else if (!isStrOrNull(v)) errors.push(`${at('timing')}.${k} must be text or null.`);
    }
  }

  if (d.facts !== undefined && d.facts !== null) {
    if (!Array.isArray(d.facts)) errors.push(`${at('facts')} must be a list.`);
    else d.facts.forEach((f, i) => {
      if (!isObj(f) || !isNonEmpty(f.label) || !isStr(f.value)) errors.push(`${at('facts')}[${i}] needs a label and a value.`);
    });
  }

  if (!Array.isArray(d.ingredientGroups) || d.ingredientGroups.length === 0) {
    errors.push(`${at('ingredientGroups')} needs at least one group.`);
  } else d.ingredientGroups.forEach((g, gi) => {
    const p = `${at('ingredientGroups')}[${gi}]`;
    if (!isObj(g)) { errors.push(`${p} must be an object.`); return; }
    if (!isStrOrNull(g.group)) errors.push(`${p}.group must be text or null.`);
    if (!Array.isArray(g.items) || g.items.length === 0) { errors.push(`${p}.items needs at least one ingredient.`); return; }
    g.items.forEach((it, ii) => {
      const q = `${p}.items[${ii}]`;
      if (!isObj(it)) { errors.push(`${q} must be an object.`); return; }
      if (!isNonEmpty(it.name)) errors.push(`${q}.name is required.`);
      if (!isStrOrNull(it.quantity)) errors.push(`${q}.quantity must be text or null.`);
      if (!isStrOrNull(it.note)) errors.push(`${q}.note must be text or null.`);
      if (!isNumOrNull(it.grams)) errors.push(`${q}.grams must be a number or null.`);
      else if (isNum(it.grams) && it.grams < 0) errors.push(`${q}.grams cannot be negative.`);
    });
  });

  if (!Array.isArray(d.methodSections) || d.methodSections.length === 0) {
    errors.push(`${at('methodSections')} needs at least one section.`);
  } else d.methodSections.forEach((s, si) => {
    const p = `${at('methodSections')}[${si}]`;
    if (!isObj(s)) { errors.push(`${p} must be an object.`); return; }
    if (!isStrOrNull(s.phase)) errors.push(`${p}.phase must be text or null.`);
    if (!Array.isArray(s.steps) || s.steps.length === 0) errors.push(`${p}.steps needs at least one step.`);
    else s.steps.forEach((st, i) => { if (!isNonEmpty(st)) errors.push(`${p}.steps[${i}] must be non-empty text.`); });
  });

  if (d.notes !== undefined && d.notes !== null) {
    if (!Array.isArray(d.notes)) errors.push(`${at('notes')} must be a list.`);
    else d.notes.forEach((n, i) => {
      if (!isObj(n) || !MARGIN_NOTE_TYPES.includes(n.type) || !isNonEmpty(n.body)) {
        errors.push(`${at('notes')}[${i}] needs a type (${MARGIN_NOTE_TYPES.join(', ')}) and a body.`);
      }
    });
  }

  validateSafety(d.safety, errors, warnings);
}

function validateSafety(s, errors, warnings) {
  if (s === undefined || s === null) return; // a fresh sausage with no cure is legitimate
  const p = 'display.safety';
  if (!isObj(s)) { errors.push(`${p} must be an object.`); return; }

  if (s.cure !== undefined) {
    if (!Array.isArray(s.cure)) errors.push(`${p}.cure must be a list.`);
    else s.cure.forEach((c, i) => {
      const q = `${p}.cure[${i}]`;
      if (!isObj(c) || !isNonEmpty(c.name)) { errors.push(`${q} needs a name.`); return; }
      if (typeof c.withheld !== 'boolean') errors.push(`${q}.withheld must be true or false.`);
      if (!isStrOrNull(c.amountText)) errors.push(`${q}.amountText must be text or null.`);
      if (!isNumOrNull(c.grams)) errors.push(`${q}.grams must be a number or null.`);
      if (c.withheld) warnings.push(`${c.name}: the calculator withheld the amount. The page will say so; nothing is blocked.`);
    });
  }

  const n = s.nitrite;
  if (n !== undefined && n !== null) {
    if (!isObj(n)) errors.push(`${p}.nitrite must be an object or null.`);
    else {
      if (!isNumOrNull(n.ppm)) errors.push(`${p}.nitrite.ppm must be a number or null.`);
      if (!isNumOrNull(n.limitPpm)) errors.push(`${p}.nitrite.limitPpm must be a number or null.`);
      if (!isNumOrNull(n.tolerancePpm)) errors.push(`${p}.nitrite.tolerancePpm must be a number or null.`);
      for (const k of ['text', 'level', 'basis', 'note']) if (!isStrOrNull(n[k])) errors.push(`${p}.nitrite.${k} must be text or null.`);
      // Warnings only — never errors. The calculator is the check.
      if (isNum(n.ppm) && isNum(n.limitPpm) && n.ppm > n.limitPpm + (isNum(n.tolerancePpm) ? n.tolerancePpm : 0)) {
        warnings.push(`Nitrite ${n.text || n.ppm + ' ppm'} is above the ${n.limitPpm} ppm reference${n.basis ? ` (${n.basis})` : ''}. It will be shown as-is.`);
      } else if (n.level && !['LOW', 'TARGET', 'OK'].includes(n.level)) {
        warnings.push(`Nitrite badge is ${n.level}. It will be shown as-is.`);
      }
    }
  }
  if (!isStrOrNull(s.disclaimer)) errors.push(`${p}.disclaimer must be text or null.`);
  if (Array.isArray(s.cure) && s.cure.length && !isNonEmpty(s.disclaimer)) warnings.push('A cure is listed but there is no safety disclaimer.');
  if (s.warnings !== undefined) {
    if (!Array.isArray(s.warnings) || !s.warnings.every(isStr)) errors.push(`${p}.warnings must be a list of text.`);
    else warnings.push(...s.warnings);
  }
}

// ── comparing an edited display against the original ──────────────────────

/**
 * Amount-bearing differences between the exported display and the one the
 * cook edited in the import preview. Words (names, steps, notes) are free to
 * change; amounts are what make the page disagree with the calculator file.
 * @returns {string[]} human-readable list; empty = amounts untouched
 */
export function amountChanges(original, edited) {
  const out = [];
  const flat = (d) => (d?.ingredientGroups || []).flatMap((g) => (g.items || []).map((it) => ({ g: g.group || '', ...it })));
  const a = flat(original);
  const b = flat(edited);
  const key = (x) => `${x.g}\u0000${x.name}`;
  const byKey = new Map(a.map((x) => [key(x), x]));
  for (const x of b) {
    const o = byKey.get(key(x));
    if (!o) { out.push(`Added or renamed: ${x.name}${x.quantity ? ` (${x.quantity})` : ''}`); continue; }
    byKey.delete(key(x));
    if ((o.quantity ?? '') !== (x.quantity ?? '') || (o.grams ?? null) !== (x.grams ?? null)) {
      out.push(`${x.name}: ${o.quantity ?? '—'} → ${x.quantity ?? '—'}`);
    }
  }
  for (const o of byKey.values()) out.push(`Removed or renamed: ${o.name}`);
  const cure = (d) => JSON.stringify((d?.safety?.cure || []).map((c) => [c.name, c.amountText ?? null, c.grams ?? null, !!c.withheld]));
  if (cure(original) !== cure(edited)) out.push('Cure amount changed');
  const ppm = (d) => JSON.stringify(d?.safety?.nitrite ?? null);
  if (ppm(original) !== ppm(edited)) out.push('Nitrite figures changed');
  return out;
}

// ── mapping to a Mesa Robles entry (frontmatter object) ────────────────────

/**
 * @param {object} pkg       a package that passed validatePackage()
 * @param {object} choices   what the cook picked on the import page
 *   { title, slug, department, section, visibility, source_type, origin, memory,
 *     complexity, photos, display (edited copy, defaults to pkg.display),
 *     packagePath (defaults to /recipes/<slug>/package) }
 */
export function toEntry(pkg, choices = {}) {
  const d = choices.display || pkg.display;
  const slug = choices.slug || d.suggested?.slug || slugify(choices.title || d.title);
  const edits = choices.display ? amountChanges(pkg.display, choices.display) : [];

  const ingredients = [];
  let order = 0;
  for (const g of d.ingredientGroups) {
    for (const it of g.items) {
      order += 1;
      ingredients.push({
        group: g.group ?? null,
        name: it.name,
        quantity: it.quantity ?? null,
        note: it.note ?? null,
        sort_order: order,
      });
    }
  }

  const steps = [];
  let n = 0;
  for (const s of d.methodSections) {
    s.steps.forEach((body, i) => {
      n += 1;
      steps.push({ step_number: n, phase_label: i === 0 ? (s.phase ?? null) : null, body, sort_order: n });
    });
  }

  const timing = {};
  for (const k of TIMING_KEYS) timing[k] = d.timing?.[k] ?? null;

  return {
    title: choices.title || d.title,
    slug,
    entry_type: 'recipe',
    published: true,
    visibility: VISIBILITY.includes(choices.visibility) ? choices.visibility : 'unlisted',
    department: choices.department ?? d.suggested?.department ?? null,
    section: choices.section ?? d.suggested?.section ?? null,
    source_type: choices.source_type ?? null,
    origin: choices.origin ?? null,
    memory: choices.memory !== undefined ? choices.memory : (d.summary ?? null),
    subtitle: d.subtitle ?? null,
    complexity: choices.complexity ?? null,
    timing,
    facts: (d.facts || []).map((f) => ({ label: f.label, value: f.value })),
    ingredients,
    steps,
    margin_notes: (d.notes || []).map((x, i) => ({
      type: x.type, body: x.body, position: null, sort_order: i + 1, from_package: true,
    })),
    safety: d.safety ? {
      cure: (d.safety.cure || []).map((c) => ({
        name: c.name, amount_text: c.amountText ?? null, grams: c.grams ?? null, withheld: !!c.withheld,
      })),
      nitrite: d.safety.nitrite ? {
        ppm: d.safety.nitrite.ppm ?? null,
        text: d.safety.nitrite.text ?? null,
        level: d.safety.nitrite.level ?? null,
        limit_ppm: d.safety.nitrite.limitPpm ?? null,
        tolerance_ppm: d.safety.nitrite.tolerancePpm ?? null,
        basis: d.safety.nitrite.basis ?? null,
        note: d.safety.nitrite.note ?? null,
      } : null,
      disclaimer: d.safety.disclaimer ?? null,
      warnings: d.safety.warnings || [],
    } : null,
    allergens: d.allergens ?? null,
    elevation_ft: d.elevationFt ?? null,
    rescale_note: d.rescaleNote ?? null,
    calculator: {
      tool: APP_TOOL[pkg.source.app],
      url: pkg.source.appUrl,
      package: choices.packagePath || `/recipes/${slug}/package`,
      exported_at: pkg.exportedAt,
      build: pkg.source.build ?? null,
      amounts_edited: edits.length > 0,
    },
    photos: choices.photos || [],
    ebook_eligible: false,
  };
}

/**
 * Choices to pre-fill the import form with when re-importing onto an existing
 * page, so the cook starts from what the site already says.
 */
export function choicesFromEntry(existing) {
  return {
    title: existing.title, slug: existing.slug,
    department: existing.department, section: existing.section,
    visibility: existing.visibility ?? 'public',
    source_type: existing.source_type ?? null, origin: existing.origin ?? null,
    memory: existing.memory ?? null, complexity: existing.complexity ?? null,
  };
}

/**
 * Re-import onto an existing page. `incoming` is toEntry() built with choices
 * that started from choicesFromEntry(existing). Amounts, steps, facts and
 * safety come from the new package; the site-only parts are kept: existing
 * photos (new ones are appended), hand-written margin notes, ebook/print
 * fields and any field this format does not know about.
 */
export function mergeUpdate(existing, incoming) {
  const keptNotes = (existing.margin_notes || []).filter((x) => !x.from_package);
  const notes = [...keptNotes, ...(incoming.margin_notes || [])].map((x, i) => ({ ...x, sort_order: i + 1 }));
  return {
    ...existing,
    ...incoming,
    slug: existing.slug,
    photos: [...(existing.photos || []), ...(incoming.photos || [])],
    margin_notes: notes,
    ebook_eligible: existing.ebook_eligible ?? false,
  };
}

/** Entry problems that would make a page render wrong or vanish from listings. */
export function validateEntry(e) {
  const errors = [];
  if (!isNonEmpty(e.title)) errors.push('Title is required.');
  if (!SLUG_RE.test(e.slug || '')) errors.push('Slug must be lowercase letters, numbers and dashes.');
  if (!isValidPlacement(e.department, e.section)) errors.push('Choose a department and a section that belongs to it.');
  else if (NOT_IMPORT_TARGETS.includes(e.section)) errors.push(`"${e.section}" is the calculator's own landing page; choose another section.`);
  if (!VISIBILITY.includes(e.visibility)) errors.push('Choose Public or Unlisted.');
  if (e.source_type && !SOURCE_TYPES.includes(e.source_type)) errors.push('Unknown source type.');
  const nums = e.steps.map((s) => s.step_number);
  if (new Set(nums).size !== nums.length) errors.push('Step numbers must be unique.');
  return errors;
}

// ── YAML frontmatter ───────────────────────────────────────────────────────
// A tiny emitter for plain data. Strings are written as JSON strings, which are
// valid YAML double-quoted scalars, so no escaping rules of our own.

export function toYaml(value, indent = 0) {
  const pad = ' '.repeat(indent);
  const scalar = (v) => {
    if (v === null || v === undefined) return 'null';
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    if (typeof v === 'number') return Number.isFinite(v) ? String(v) : 'null';
    return JSON.stringify(String(v));
  };
  const isLeaf = (v) => v === null || v === undefined || typeof v !== 'object';
  if (isLeaf(value)) return scalar(value);

  if (Array.isArray(value)) {
    if (!value.length) return '[]';
    return value.map((item) => {
      if (isLeaf(item)) return `${pad}- ${scalar(item)}`;
      if (Array.isArray(item)) return item.length ? `${pad}-\n${toYaml(item, indent + 2)}` : `${pad}- []`;
      const body = toYaml(item, indent + 2);
      if (body === '{}') return `${pad}- {}`;
      return `${pad}- ${body.slice(indent + 2)}`;
    }).join('\n');
  }

  const keys = Object.keys(value);
  if (!keys.length) return '{}';
  return keys.map((k) => {
    const v = value[k];
    const key = /^[A-Za-z_][A-Za-z0-9_]*$/.test(k) ? k : JSON.stringify(k);
    if (isLeaf(v)) return `${pad}${key}: ${scalar(v)}`;
    const inner = toYaml(v, indent + 2);
    if (inner === '[]' || inner === '{}') return `${pad}${key}: ${inner}`;
    return `${pad}${key}:\n${inner}`;
  }).join('\n');
}

/** The full .md file Mesa Robles commits: frontmatter only, no body. */
export function toMarkdown(entry) {
  return `---\n${toYaml(entry)}\n---\n`;
}
